import { useEffect, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import {
  WebsocketSummarySessionResponse,
  AppendTextToIdeaNodeResponse,
} from "../types/websocket_types";
import { getExtensionWsToken } from "@sharpana/auth/api_client/allauth";
import { isWebExtension as _isWebExtension } from "@sharpana/shared/utils/web_environment";

interface UseWebSocketConnectionOptions {
  wsURL: string;
}

// Define stream states for better tracking
export enum StreamState {
  IDLE = "idle",
  IN_PROGRESS = "in_progress",
  STALLED = "stalled",
  FINISHED = "finished"
}

interface UseWebSocketConnectionReturn {
  sendJsonMessage: (message: any) => void;
  lastJsonMessage: WebsocketSummarySessionResponse | null;
  readyState: ReadyState;
  streamState: StreamState;
  reconnectWs: () => void;
  socketUrl: string;
}

interface ExtensionWebSocketTokenResponse {
  token: string;
  expires_at: string;
}

export default function useWebSocketConnection({
  wsURL,
}: UseWebSocketConnectionOptions): UseWebSocketConnectionReturn {
  const [socketUrl, setSocketUrl] = useState(wsURL);
  const [streamState, setStreamState] = useState<StreamState>(StreamState.IDLE);
  const lastConnectionTime = useRef<number>(Date.now());

  const [extensionToken, setExtensionToken] =
    useState<ExtensionWebSocketTokenResponse | null>(null);

  const streamTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isWebExtension = _isWebExtension();

  /**
   * Manual Reconnection Handler
   */
  const reconnectWs = () => {
    setSocketUrl("");
    // A useEffect hook takes care of reconnecting when the socketUrl becomes == ""
  };

  /**
   * Whenever we want to "force" a fresh WebSocket connection, if socketUrl is "", we set it back to the desired URL.
   * This useEffect is also used to ensure that if we are in WebExtension mode and have a token, we refresh the final wsURL.
   */
  useEffect(() => {
    if (socketUrl === "") {
      // console.log(
      //   "isWebExtension and extensionToken",
      //   isWebExtension,
      //   extensionToken
      // );

      if (
        isWebExtension &&
        extensionToken &&
        new Date(extensionToken.expires_at) < new Date()
      ) {
        setSocketUrl(`${wsURL}?token=${extensionToken.token}`);
      } else if (isWebExtension) {
        // If we don't have a token or the token is expired, we need to fetch a new one
        getExtensionWsToken().then((data: ExtensionWebSocketTokenResponse) => {
          setExtensionToken(data);
          setSocketUrl(`${wsURL}?token=${data.token}`);
        });
      } else {
        setSocketUrl(wsURL);
      }
    }
  }, [socketUrl, wsURL, isWebExtension, extensionToken]);

  /**
   * WebSocket Hook Configuration
   *
   * Different reconnection strategies are needed for the web extension vs the web app standalone
   * That's because of browser throttling behavior on regular tabs/windows vs on extension environments.
   *
   * Browsers often throttle or suspend WebSocket connections in inactive/background standalone tabs
   * to conserve resources (so the client might think the connection is closing, but it actually isn't),
   * This can cause a code 1000 "normal" WS close event, which would trigger a reconnect given our reconnectAttempts setting below.
   * 
   * In particular, Chrome automatically "closes" WebSocket after ~2 seconds if it thinks the tab is "inactive", even though our tab is visible, the browser might consider it "inactive" without user interaction
   * or ws events, which is the browser's desire to conserve resources when it thinks the connection isn't actively needed.
   * 
   * So, to prevent this from triggering a disconnect/reconnect infinite loop, we ignore reconnecting inside "shouldReconnect" (since the connection is actually still present, just managed more efficiently by the browser) when
   * the client receives a close event with code=1000 (clean close) or we are not actively streaming (if we are actively streaming, and we receive a close event we want to reconnect)
   
   * 
   * - Extension: Always reconnect 
   * - Standalone: Only reconnect if:
   *   1. Actively streaming OR
   *   2. Tab is visible AND connection was cleanly closed
   */
  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
    socketUrl,
    {
      shouldReconnect: (closeEvent) => {
        if (closeEvent?.code === 4003 && isWebExtension) {
          // 4003 is unauthorized, which we get when the token expires
          getExtensionWsToken().then(
            (data: ExtensionWebSocketTokenResponse) => {
              setExtensionToken(data);
            }
          );
          return true;
        }

        // if (isInIframe) {
        //   return true;
        // }

        // Standalone mode: ignore Chrome's "inactivity" throttling
        // at some point attempted to do it based on (closeEvent?.code == 1000 && closeEvent?.wasClean), but was also seeing other close state codes like 1006 (abnormal closure)
        // so instead we just check if the connection is open or connecting, and if so, we don't reconnect (since the connection is still good)
        if (
          readyState === ReadyState.OPEN ||
          readyState === ReadyState.CONNECTING
        ) {
          return false;
        }

        return true;
      },
      reconnectAttempts: 5,
      reconnectInterval: (attemptNumber) =>
        Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
      onOpen: () => {
        lastConnectionTime.current = Date.now();
      },
      onClose: () => {
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
        }
      },
      onError: () => {},
    }
  );

  /**
   * Streaming State Management
   * (Currently commented out, but left here for future reference)
   *
   * Pure message-based streaming state tracking:
   * - Start streaming when first chunk arrives
   * - End streaming when final chunk arrives
   * - Safety timeout in case we miss the end message
   */
  useEffect(() => {
    if (!lastJsonMessage) return;

    const message = lastJsonMessage as WebsocketSummarySessionResponse;
    if (message.rehydrate) return;

    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
    }

    if ("finished_stream" in message) {
      const streamingMessage = message as AppendTextToIdeaNodeResponse;

      if (streamingMessage.finished_stream === false) {
        setStreamState(StreamState.IN_PROGRESS);
        // Set a safety timeout in case we miss the finished_stream=true message
        streamTimeoutRef.current = setTimeout(() => {
          setStreamState(StreamState.STALLED);
        }, 45000); // 45 second safety timeout [if we don't receive a chunk in this time, we assume the stream has stalled]
      } else if (streamingMessage.finished_stream === true) {
        setStreamState(StreamState.FINISHED);
        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
        }
      }
    }
  }, [lastJsonMessage]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
      }
    };
  }, []);

  return {
    sendJsonMessage,
    lastJsonMessage,
    readyState,
    streamState,
    reconnectWs,
    socketUrl,
  };
}
